import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '@/components/ui/Icon';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { toast } from '@/utils/toast';
import { useTimezone } from '@/contexts/TimezoneContext';
import { statusService } from '@/services/status.service';
import { SystemIncident, IncidentUpdate } from '@/types/status';

/**
 * SystemIncident Manager
 *
 * Admin interface for creating and managing incidents
 */

type IncidentStatus = 'investigating' | 'identified' | 'monitoring' | 'resolved';

const INCIDENT_STATUS_OPTIONS = [
  { value: 'investigating', label: 'Investigating', icon: 'search' },
  { value: 'identified', label: 'Identified', icon: 'tick' },
  { value: 'monitoring', label: 'Monitoring', icon: 'pulse' },
  { value: 'resolved', label: 'Resolved', icon: 'tick-circle' },
];

const STATUS_BADGE_CONFIG: Record<IncidentStatus, { color: string; bgColor: string; label: string }> = {
  investigating: { color: 'text-red-700', bgColor: 'bg-red-100', label: 'Investigating' },
  identified: { color: 'text-amber-700', bgColor: 'bg-amber-100', label: 'Identified' },
  monitoring: { color: 'text-blue-700', bgColor: 'bg-blue-100', label: 'Monitoring' },
  resolved: { color: 'text-green-700', bgColor: 'bg-green-100', label: 'Resolved' },
};

export const IncidentManager = () => {
  const queryClient = useQueryClient();
  const { formatDateTime } = useTimezone();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [selectedSystemIncident, setSelectedSystemIncident] = useState<SystemIncident | null>(null);

  // Create form state
  const [createForm, setCreateForm] = useState({
    title: '',
    message: '',
    status: 'investigating' as IncidentStatus,
    affectedServices: [] as string[],
  });

  const [updateMessage, setUpdateMessage] = useState('');
  const [resolveMessage, setResolveMessage] = useState('');

  // Fetch incidents
  const { data: incidents, isLoading } = useQuery({
    queryKey: ['admin-incidents'],
    queryFn: statusService.getAdminIncidents,
    staleTime: 30 * 1000,
  });

  // Fetch services for multi-select
  const { data: services } = useQuery({
    queryKey: ['admin-services'],
    queryFn: statusService.getAdminServices,
    staleTime: 60 * 1000,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: statusService.createIncident,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-incidents'] });
      queryClient.invalidateQueries({ queryKey: ['system-status'] });
      toast.success('SystemIncident created');
      setCreateModalOpen(false);
      setCreateForm({ title: '', message: '', status: 'investigating', affectedServices: [] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to create incident: ${error.message}`);
    },
  });

  // Add update mutation
  const addUpdateMutation = useMutation({
    mutationFn: ({ id, message }: { id: number; message: string }) =>
      statusService.addIncidentUpdate(id, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-incidents'] });
      queryClient.invalidateQueries({ queryKey: ['system-status'] });
      toast.success('Update added');
      setUpdateModalOpen(false);
      setUpdateMessage('');
      setSelectedSystemIncident(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to add update: ${error.message}`);
    },
  });

  // Resolve mutation
  const resolveMutation = useMutation({
    mutationFn: ({ id, message }: { id: number; message: string }) =>
      statusService.resolveIncident(id, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-incidents'] });
      queryClient.invalidateQueries({ queryKey: ['system-status'] });
      toast.success('SystemIncident resolved');
      setResolveModalOpen(false);
      setResolveMessage('');
      setSelectedSystemIncident(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to resolve incident: ${error.message}`);
    },
  });

  const handleCreate = () => {
    if (!createForm.title.trim() || !createForm.message.trim()) {
      toast.error('Title and message are required');
      return;
    }

    createMutation.mutate(createForm);
  };

  const handleAddUpdate = () => {
    if (!selectedSystemIncident || !updateMessage.trim()) {
      toast.error('Update message is required');
      return;
    }

    addUpdateMutation.mutate({ id: selectedSystemIncident.id, message: updateMessage });
  };

  const handleResolve = () => {
    if (!selectedSystemIncident || !resolveMessage.trim()) {
      toast.error('Resolution message is required');
      return;
    }

    resolveMutation.mutate({ id: selectedSystemIncident.id, message: resolveMessage });
  };

  // Separate active and resolved incidents
  const activeSystemIncidents = incidents?.filter((i: SystemIncident) => !i.resolvedAt) || [];
  const resolvedSystemIncidents = incidents?.filter((i: SystemIncident) => i.resolvedAt) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name={ICONS.LOADER} size="lg" color="text-gray-400" ariaHidden />
        <span className="ml-2 text-gray-600">Loading incidents...</span>
      </div>
    );
  }

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">SystemIncidents</h2>
            <button
              onClick={() => setCreateModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-primary-600-contrast rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Icon name={ICONS.PLUS_CIRCLE} size="sm" color="text-primary-600-contrast" ariaHidden />
              Create SystemIncident
            </button>
          </div>

          {/* Active SystemIncidents */}
          {activeSystemIncidents.length > 0 && (
            <div className="mb-8">
              <h3 className="text-md font-semibold text-gray-900 mb-3">Active SystemIncidents</h3>
              <div className="space-y-4">
                {activeSystemIncidents.map((incident: SystemIncident) => {
                  const statusConfig = STATUS_BADGE_CONFIG[incident.status as IncidentStatus];
                  return (
                    <div
                      key={incident.id}
                      className="p-4 border border-gray-200 rounded-xl hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-1">{incident.title}</h4>
                          <p className="text-sm text-gray-600 mb-2">{incident.message}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={cn(
                                'inline-flex items-center px-2 py-1 rounded-md text-xs font-medium',
                                statusConfig.color,
                                statusConfig.bgColor
                              )}
                            >
                              {statusConfig.label}
                            </span>
                            {incident.affectedServices.map((service) => (
                              <span
                                key={service}
                                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium text-gray-700 bg-gray-100"
                              >
                                {service}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setSelectedSystemIncident(incident);
                              setUpdateModalOpen(true);
                            }}
                            className="px-3 py-1.5 text-sm text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                          >
                            Add Update
                          </button>
                          <button
                            onClick={() => {
                              setSelectedSystemIncident(incident);
                              setResolveModalOpen(true);
                            }}
                            className="px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          >
                            Resolve
                          </button>
                        </div>
                      </div>

                      {/* Updates Timeline */}
                      {incident.updates && incident.updates.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Updates</p>
                          <div className="space-y-2">
                            {incident.updates.map((update: IncidentUpdate) => (
                              <div key={update.id} className="flex gap-2">
                                <Icon name={ICONS.CLOCK} size="sm" color="text-gray-400" ariaHidden />
                                <div className="flex-1">
                                  <p className="text-sm text-gray-700">{update.message}</p>
                                  <p className="text-xs text-gray-500">{formatDateTime(update.createdAt)}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Resolved SystemIncidents */}
          {resolvedSystemIncidents.length > 0 && (
            <div>
              <h3 className="text-md font-semibold text-gray-900 mb-3">Resolved SystemIncidents</h3>
              <div className="space-y-4">
                {resolvedSystemIncidents.map((incident: SystemIncident) => (
                  <div
                    key={incident.id}
                    className="p-4 bg-gray-50 border border-gray-200 rounded-xl opacity-75"
                  >
                    <h4 className="font-semibold text-gray-900 mb-1">{incident.title}</h4>
                    <p className="text-sm text-gray-600 mb-2">{incident.message}</p>
                    <p className="text-xs text-gray-500">
                      Resolved {formatDateTime(incident.resolvedAt!)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(!incidents || incidents.length === 0) && (
            <p className="text-center text-gray-500 py-8">No incidents</p>
          )}
        </div>
      </div>

      {/* Create SystemIncident Modal */}
      <Modal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        title="Create SystemIncident"
        footer={
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setCreateModalOpen(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-primary-600-contrast rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Title"
            value={createForm.title}
            onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
            placeholder="Brief incident description"
            required
          />

          <Textarea
            label="Message"
            value={createForm.message}
            onChange={(e) => setCreateForm({ ...createForm, message: e.target.value })}
            placeholder="Detailed incident description"
            rows={4}
            required
          />

          <Select
            label="Status"
            options={INCIDENT_STATUS_OPTIONS}
            value={createForm.status}
            onValueChange={(value) => setCreateForm({ ...createForm, status: value as IncidentStatus })}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Affected Services
            </label>
            <div className="space-y-2">
              {services?.map((service: { slug: string; name: string }) => (
                <label key={service.slug} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={createForm.affectedServices.includes(service.slug)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setCreateForm({
                          ...createForm,
                          affectedServices: [...createForm.affectedServices, service.slug],
                        });
                      } else {
                        setCreateForm({
                          ...createForm,
                          affectedServices: createForm.affectedServices.filter((s) => s !== service.slug),
                        });
                      }
                    }}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">{service.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* Add Update Modal */}
      <Modal
        open={updateModalOpen}
        onOpenChange={setUpdateModalOpen}
        title="Add SystemIncident Update"
        footer={
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setUpdateModalOpen(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddUpdate}
              disabled={addUpdateMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-primary-600-contrast rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {addUpdateMutation.isPending ? 'Adding...' : 'Add Update'}
            </button>
          </div>
        }
      >
        <Textarea
          label="Update Message"
          value={updateMessage}
          onChange={(e) => setUpdateMessage(e.target.value)}
          placeholder="What's the current status?"
          rows={4}
          required
        />
      </Modal>

      {/* Resolve SystemIncident Modal */}
      <Modal
        open={resolveModalOpen}
        onOpenChange={setResolveModalOpen}
        title="Resolve SystemIncident"
        footer={
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setResolveModalOpen(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleResolve}
              disabled={resolveMutation.isPending}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {resolveMutation.isPending ? 'Resolving...' : 'Resolve'}
            </button>
          </div>
        }
      >
        <Textarea
          label="Resolution Message"
          value={resolveMessage}
          onChange={(e) => setResolveMessage(e.target.value)}
          placeholder="How was this incident resolved?"
          rows={4}
          required
        />
      </Modal>
    </>
  );
};
